import { buildXlsxBytes, buildXlsxFromRuntimeSheets } from './parseSheets';
import type { FortuneSheet } from './types';
import {
    composeVersionedDocId,
    docIdHasVersionHistory,
    getBlobDownloadUrl,
    parseVersionedDocId,
    STORAGE_DOC_ID,
    uploadBlobVersion,
} from '../storage/mnemosyneBlobStorage';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// The mnemosyne/S3 mechanics this module used to own now live in
// `common/storage/mnemosyneBlobStorage.ts`, shared with PDF storage (RECUR-28003).
// What stays here is everything sheet-specific: the xlsx serialization and the
// `S3_XLSX` ref shape with its read-boundary guard.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SheetStorageRef {
    type: 'S3_XLSX';
    /**
     * Either `"<mnemosyneDocId>_<versionNumber>"` (e.g. "1234_2") when the
     * bucket keeps versions, or the bare `"<mnemosyneDocId>"` (e.g. "1234")
     * when it does not — see the two storage modes below. The raw S3 versionId
     * never leaves mnemosyne; it maps sequential numbers to object versions in
     * its own doc_versions table.
     */
    docId: string;
}

/**
 * Runtime guard for storage refs arriving from the document API (untyped).
 * Every field the READ path depends on must be present and well-formed — a
 * partial ref must be rejected here (falling back to inline content) rather
 * than surfacing as a confusing mnemosyne error. Deliberately NO back-compat
 * for the old `{s3Path,bucket,fileName,sizeKb}` shape — it never reached prod,
 * so an old dev/staging row just falls back to content-less (acceptable).
 * Mirrors the documented contract on aica's OnePager.storage / DocumentRequest.storage.
 */
export const isSheetStorageRef = (value: unknown): value is SheetStorageRef => {
    if (value == null || typeof value !== 'object') return false;
    const ref = value as Partial<SheetStorageRef>;
    return ref.type === 'S3_XLSX' && typeof ref.docId === 'string' && STORAGE_DOC_ID.test(ref.docId);
};

export { composeVersionedDocId, parseVersionedDocId };

/** True when this ref names a specific version — i.e. the lineage has history. */
export const hasVersionHistory = (ref?: SheetStorageRef | null): boolean => docIdHasVersionHistory(ref?.docId);

// ---------------------------------------------------------------------------
// Feature kill-switch
// ---------------------------------------------------------------------------

// Master switch for S3-backed sheet storage. Ship-ordering safety: mnemosyne
// (v2/saveMetadata version-register mode + `version` on `downloadFilePresigned`) must
// deploy BEFORE aica #1794, which must deploy BEFORE this flag goes live — else
// sheet saves would try to persist a storage ref the backend can't yet resolve.
// Flip to false to fully disable and fall back to legacy inline save everywhere.
const SHEET_S3_STORAGE_ENABLED = true;
export const isSheetS3StorageEnabled = (): boolean => SHEET_S3_STORAGE_ENABLED;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface UploadSheetVersionArgs {
    /** Required unless `rawBytes` is given. */
    sheets?: FortuneSheet[];
    /** Already-xlsx bytes (e.g. a freshly-imported .xlsx file) — uploaded
     *  as-is, skipping serialization entirely. Takes precedence over `sheets`. */
    rawBytes?: ArrayBuffer;
    assesseeOrgId: string;
    investorOrgId?: string;
    existing?: SheetStorageRef;
    /** `sheets` is RUNTIME shape (2D `data`, straight from `getAllSheets()`) —
     *  serialize via the combined worker job (toCelldataSheet + buildXlsxBuffer
     *  in one round trip) instead of assuming celldata form is already built. */
    runtimeSheets?: boolean;
    /** Autosave tick: re-points the lineage's live draft version in place
     *  instead of finalizing it. Absent/false = commit. */
    isDraft?: boolean;
}

/**
 * Serialize sheets to xlsx (or reuse already-xlsx `rawBytes` as-is) and store the
 * bytes on the lineage's mnemosyne-owned S3 key. Returns the storage ref the
 * caller persists on the aica document row.
 */
export const uploadSheetVersion = async ({
    sheets,
    rawBytes,
    assesseeOrgId,
    investorOrgId,
    existing,
    runtimeSheets,
    isDraft,
}: UploadSheetVersionArgs): Promise<SheetStorageRef> => {
    if (!rawBytes && !sheets) {
        throw new Error('uploadSheetVersion requires either sheets or rawBytes');
    }
    // An import's rawBytes ARE already the xlsx we persist (skip serializing
    // entirely); otherwise serialize `sheets`.
    const bytes =
        rawBytes ??
        (runtimeSheets ? await buildXlsxFromRuntimeSheets(sheets as FortuneSheet[]) : await buildXlsxBytes(sheets as FortuneSheet[]));

    const docId = await uploadBlobVersion({
        bytes,
        mime: XLSX_MIME,
        extension: 'xlsx',
        fileNamePrefix: 'sheet',
        assesseeOrgId,
        investorOrgId,
        existingDocId: existing?.docId,
        isDraft,
    });

    return { type: 'S3_XLSX', docId };
};

/**
 * Get a presigned download URL for a stored sheet. A ref naming a version
 * resolves to exactly those bytes; a version-less ref (unversioned bucket)
 * resolves to whatever the key currently holds.
 */
export const getSheetDownloadUrl = async (ref: SheetStorageRef): Promise<string> => {
    if (!ref?.docId) {
        throw new Error('Cannot download a sheet without a storage docId');
    }
    return getBlobDownloadUrl(ref.docId);
};
